import React from 'react';

/**
 * EdgeContainmentFilterControl - Control for edge containment filters
 *
 * Edges are added via graph interaction. This control allows:
 * - Enabling/disabling the filter
 * - Setting match type (any/all)
 * - Setting mode (include/exclude)
 * - Viewing and removing selected edges
 *
 * @param {object} value - Current filters { enabled, edges, matchType, mode }
 * @param {Function} onChange - Callback when filters change
 * @param {boolean} disabled - Whether control is disabled
 */
const EdgeContainmentFilterControl = ({
  value = { enabled: false, edges: [], matchType: 'any', mode: 'include' },
  onChange,
  disabled = false,
  onAddEdge,
}) => {
  const handleEnabledChange = (enabled) => {
    if (disabled) return;
    onChange({ ...value, enabled });
  };

  const handleMatchTypeChange = (matchType) => {
    if (disabled) return;
    onChange({ ...value, matchType });
  };

  const handleModeChange = (mode) => {
    if (disabled) return;
    onChange({ ...value, mode });
  };

  const handleRemoveEdge = (index) => {
    if (disabled) return;
    const newEdges = value.edges.filter((_, i) => i !== index);
    onChange({ ...value, edges: newEdges });
  };

  const formatEdgeLabel = (edge) => {
    if (edge.edgeLabel) {
      return edge.edgeLabel;
    }
    return `${edge.sourceActivity} → ${edge.targetActivity}`;
  };

  return (
    <div className="space-y-3">
      {/* Header with Enable Toggle */}
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          Edge Containment Filters
          {value.enabled && value.edges.length > 0 && (
            <span className="ml-2 text-xs text-gray-500">
              ({value.edges.length} edge{value.edges.length !== 1 ? 's' : ''})
            </span>
          )}
        </label>
        <label className="flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={value.enabled}
            onChange={(e) => handleEnabledChange(e.target.checked)}
            disabled={disabled}
            className="mr-2 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <span className="text-sm text-gray-600">Enabled</span>
        </label>
      </div>

      {/* Mode and Match Type Selection */}
      {value.edges.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-4 p-2 bg-gray-50 rounded-md">
            <span className="text-xs font-medium text-gray-700">Mode:</span>
            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                name="edge-containment-mode"
                value="include"
                checked={value.mode === 'include'}
                onChange={() => handleModeChange('include')}
                disabled={disabled || !value.enabled}
                className="mr-1.5 h-3.5 w-3.5 text-blue-600 border-gray-300 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Include</span>
            </label>
            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                name="edge-containment-mode"
                value="exclude"
                checked={value.mode === 'exclude'}
                onChange={() => handleModeChange('exclude')}
                disabled={disabled || !value.enabled}
                className="mr-1.5 h-3.5 w-3.5 text-blue-600 border-gray-300 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Exclude</span>
            </label>
          </div>

          <div className="flex items-center gap-4 p-2 bg-gray-50 rounded-md">
            <span className="text-xs font-medium text-gray-700">Match:</span>
            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                name="edge-containment-match"
                value="any"
                checked={value.matchType === 'any'}
                onChange={() => handleMatchTypeChange('any')}
                disabled={disabled || !value.enabled}
                className="mr-1.5 h-3.5 w-3.5 text-blue-600 border-gray-300 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Any</span>
            </label>
            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                name="edge-containment-match"
                value="all"
                checked={value.matchType === 'all'}
                onChange={() => handleMatchTypeChange('all')}
                disabled={disabled || !value.enabled}
                className="mr-1.5 h-3.5 w-3.5 text-blue-600 border-gray-300 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">All</span>
            </label>
          </div>
        </div>
      )}

      {/* Add Edge Button */}
      {onAddEdge && (
        <button
          type="button"
          onClick={onAddEdge}
          disabled={disabled}
          className="w-full px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          + Add Edge from Graph
        </button>
      )}

      {/* Edge List */}
      {value.edges.length > 0 ? (
        <div className="space-y-2">
          <span className="block text-xs font-medium text-gray-600">
            Selected Edges ({value.edges.length})
          </span>
          <div className="space-y-2">
            {value.edges.map((edge, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-200"
              >
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">
                    {formatEdgeLabel(edge)}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveEdge(index)}
                  disabled={disabled}
                  className="ml-2 text-red-600 hover:text-red-800 disabled:text-gray-400 disabled:cursor-not-allowed"
                  title="Remove edge"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="text-xs text-gray-500 italic p-3 bg-gray-50 rounded border border-gray-200">
          No edges selected.
          <br />
          Click the button above to select an edge from the graph.
        </div>
      )}

      {/* Help Text */}
      {value.enabled && value.edges.length > 0 && (
        <div className="text-xs text-gray-600">
          <strong>Match {value.matchType}</strong> of the selected edges ({value.mode === 'include' ? 'include' : 'exclude'} matching traces)
        </div>
      )}
    </div>
  );
};

export default EdgeContainmentFilterControl;
